import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { cmdApi } from "../api";

export default function CmdSettings() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [newVert, setNewVert] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await cmdApi.get("/settings");
    setData(data);
    setDraft(data.verticals);
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="cmd-empty"><p>Loading…</p></div>;

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.verticals);

  const add = () => {
    const v = newVert.trim();
    if (!v) return;
    if (draft.includes(v)) { toast.error("Already in the list."); return; }
    if (draft.length >= 30) { toast.error("Up to 30 verticals."); return; }
    setDraft([...draft, v]);
    setNewVert("");
  };
  const remove = (v) => setDraft(draft.filter((x) => x !== v));
  const resetDefaults = () => setDraft([...data.default_verticals]);
  const save = async () => {
    setSaving(true);
    try {
      const { data: next } = await cmdApi.put("/settings/verticals", { verticals: draft });
      toast.success("Verticals updated");
      setData((d) => ({ ...d, verticals: next.verticals, updated_at: next.updated_at }));
      setDraft(next.verticals);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cmd-settings">
      <h1 style={{ margin: "4px 0 6px", fontSize: 24, fontWeight: 700 }}>Settings</h1>
      <p className="cmd-muted" style={{ margin: "0 0 24px" }}>
        Verticals power scoping across Portfolio, Board, Financials and Reports. Renaming or removing a
        vertical that is still used by an active initiative is blocked — archive or reassign first.
      </p>

      <div className="cmd-raised" style={{ borderRadius: 14, padding: 20, maxWidth: 720 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Business verticals</h3>
          <button className="cmd-btn cmd-btn-ghost" onClick={resetDefaults}
            style={{ padding: "6px 12px", fontSize: 13 }} data-testid="settings-reset">
            Reset to defaults
          </button>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }} data-testid="settings-verticals">
          {draft.length === 0 && <span className="cmd-muted" style={{ fontSize: 13 }}>No verticals yet. Add at least one.</span>}
          {draft.map((v) => (
            <span key={v} className="cmd-chip" style={{ gap: 8, paddingRight: 6 }} data-testid={`vertical-chip-${v.replace(/\s/g,'-')}`}>
              {v}
              <button className="cmd-icon-btn" style={{ width: 20, height: 20, border: "none" }}
                aria-label={`Remove ${v}`} onClick={() => remove(v)}
                data-testid={`vertical-remove-${v.replace(/\s/g,'-')}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input className="cmd-input" style={{ flex: 1 }} placeholder="Add a vertical (e.g. Education)"
            value={newVert} onChange={(e) => setNewVert(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); }}}
            aria-label="New vertical name" data-testid="settings-new-vertical" />
          <button className="cmd-btn" onClick={add} data-testid="settings-add-vertical">
            <Plus size={16} /> Add
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button className="cmd-btn cmd-btn-ghost" onClick={() => setDraft(data.verticals)}
            disabled={!dirty} data-testid="settings-discard">
            Discard
          </button>
          <button className="cmd-btn cmd-btn-primary" onClick={save} disabled={!dirty || saving || draft.length === 0}
            data-testid="settings-save">
            {saving ? "Saving…" : "Save verticals"}
          </button>
        </div>
      </div>

      <div className="cmd-raised" style={{ borderRadius: 14, padding: 20, maxWidth: 720, marginTop: 20 }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 16, fontWeight: 600 }}>Keyboard shortcuts</h3>
        <ul className="cmd-muted" style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.9 }}>
          <li><kbd>G</kbd> then <kbd>P</kbd> — Portfolio</li>
          <li><kbd>G</kbd> then <kbd>B</kbd> — Board</li>
          <li><kbd>G</kbd> then <kbd>D</kbd> — Decisions</li>
          <li><kbd>G</kbd> then <kbd>F</kbd> — Financials</li>
          <li><kbd>G</kbd> then <kbd>R</kbd> — Reports</li>
          <li><kbd>G</kbd> then <kbd>V</kbd> — Review</li>
          <li><kbd>?</kbd> — this cheatsheet (anywhere)</li>
        </ul>
      </div>
    </div>
  );
}
